import {useCallback, useEffect, useRef, useState} from "react";
import {Link, useLocation} from "react-router-dom";
import {useI18n} from "./I18nProvider";
import {LocaleSwitcher} from "./LocaleSwitcher";
import {HeaderNav} from "./HeaderNav";
import {FreeShippingBar} from "./FreeShippingBar";
import {HeaderSearchOverlay} from "./HeaderSearchOverlay";
import {useSession} from "./SessionProvider";
import {useCartDrawer} from "./CartDrawerProvider";
import {useMobileMenu} from "./MobileMenuProvider";
import {paths} from "@/lib/paths";
import {addRecentSearch, HEADER_SEARCH_CLOSE_EVENT} from "@corporatesclub/shared";
import {getWishCountCached, setWishCountCache} from "@/lib/shopCache";

export function ShopHeader() {
  const {t} = useI18n();
  const {user} = useSession();
  const {open: openCartDrawer} = useCartDrawer();
  const {open: openMobileMenu} = useMobileMenu();
  const location = useLocation();
  const cartCount = user?.cartCount ?? 0;
  const [wishCount, setWishCount] = useState(0);
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setQ("");
    searchRef.current?.blur();
  }, []);
  useEffect(() => {
    getWishCountCached(user).then(setWishCount).catch(() => {});
    function onWish(e: Event) {
      const n = (e as CustomEvent).detail;
      if(typeof n === "number") { setWishCount(n); setWishCountCache(n); }
    }
    window.addEventListener("wishlist-updated", onWish);
    return () => {
      window.removeEventListener("wishlist-updated", onWish);
    };
  }, [user?.wishlistCount, user?.authenticated]);
  useEffect(() => {
    function onCloseSearch() { closeSearch(); }
    window.addEventListener(HEADER_SEARCH_CLOSE_EVENT, onCloseSearch);
    return () => window.removeEventListener(HEADER_SEARCH_CLOSE_EVENT, onCloseSearch);
  }, [closeSearch]);
  useEffect(() => {
    closeSearch();
  }, [location.pathname, closeSearch]);
  function openSearch() {
    setSearchOpen(true);
  }
  function onSubmit(qVal: string) {
    const s = qVal.trim();
    if(s.length < 2) return;
    addRecentSearch(s);
    setSearchOpen(true);
  }
  return (
    <>
      <FreeShippingBar />
      <header id="cc-header" className="cc-header">
        <div className="cc-header__bar">
          <div className="cc-header__container">
            <div className="cc-header__row">
              <div className="cc-header__logo">
                <Link to="/" className="cc-header__logoLink" id="logo">
                  <img src="/images/logo-dark.png" alt="CorporatesClub" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/images/logo.png"; }} />
                </Link>
              </div>
              <div className="cc-header__search">
                <form className="cc-header__searchForm" id="searchForm" action={paths.search} autoComplete="off" onSubmit={(e) => { e.preventDefault(); onSubmit(q); }}>
                  <div className="cc-header__searchField">
                    <span className="cc-header__searchIcon" aria-hidden="true">
                      <i className="fa fa-search" />
                    </span>
                    <input
                      ref={searchRef}
                      type="search"
                      className="cc-header__searchInput"
                      id="sugestiiCautare"
                      name="s"
                      placeholder={t("Search for products...")}
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      onFocus={openSearch}
                      onClick={openSearch}
                      readOnly={searchOpen}
                    />
                    <button type="submit" className="cc-header__searchSubmit" aria-label={t("Search for products...")}>
                      <i className="fa fa-chevron-right" aria-hidden="true" />
                    </button>
                  </div>
                </form>
              </div>
              <div className="cc-header__actions">
                <button type="button" className="cc-header__icon" id="cart" title={t("Shopping cart")} onClick={openCartDrawer}>
                  <i className="fa fa-shopping-bag" aria-hidden="true" />
                  {cartCount > 0 ? <span className="cc-header__iconBadge">{cartCount}</span> : null}
                </button>
                <Link to={paths.wishlist} className="cc-header__icon" id="wishlistButton" title={t("Wishlist")}>
                  <i className={`fa ${wishCount > 0 ? "fa-heart" : "fa-heart-o"}`} aria-hidden="true" />
                  {wishCount > 0 ? <span className="cc-header__iconBadge">{wishCount}</span> : null}
                </Link>
                <Link to={paths.account} className="cc-header__icon" id="userAccount" title={t("Account")}>
                  <i className="fa fa-user" aria-hidden="true" />
                </Link>
                <div className="cc-header__localeDesktop">
                  <LocaleSwitcher />
                </div>
                <button
                  type="button"
                  className="cc-header__icon cc-header__menuMobile"
                  title={t("Menu")}
                  aria-label={t("Menu")}
                  onClick={openMobileMenu}
                >
                  <i className="fa fa-bars" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <HeaderNav />
      </header>
      <HeaderSearchOverlay open={searchOpen} query={q} onQueryChange={setQ} onClose={closeSearch} />
    </>
  );
}
